import { inject, Injector, runInInjectionContext, signal } from '@angular/core';
import type { DocumentNode } from 'graphql';
import { finalize, tap } from 'rxjs';
import type { DataTableData } from '../../../components/data-table/types';
import { queryPaginate } from '../../../modules/graphql/fetchers/query-paginate';
import type { FetchVariables } from '../../../modules/graphql/fetchers/types';

/**
 * static-graphql-documents (D3 clean break): no `fields?` parameter anymore
 * — `statement` already carries its full selection. `<T>` stays
 * caller-supplied (unlike the other 3 atoms): `paginationQuery` carries no
 * fragment (its `rows` is an untyped JSON scalar), so there is nothing to
 * infer the row type FROM.
 */
export function useGraphqlPaginate<T>(statement: DocumentNode, key: string) {
  const injector = inject(Injector);
  const pagination = signal<DataTableData<T> | null>(null);
  const loading = signal(false);

  function paginate(variables?: FetchVariables) {
    loading.set(true);
    return runInInjectionContext(injector, () =>
      queryPaginate<T>(statement, key, variables),
    ).pipe(
      tap((data) => pagination.set(data)),
      finalize(() => loading.set(false)),
    );
  }

  return {
    paginate,
    pagination: pagination.asReadonly(),
    loading: loading.asReadonly(),
  };
}
